///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../abstract-command';
import { ActivityDTO } from '../../../../services/project-socket/activity-dto.model';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthCreateMultipleEntitiesCommand } from './create-multiple-entities-command';
import { GtthRemoveMultipleEntitiesCommand } from './remove-multiple-entities-command';
import { GtthLinkFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthModifyNodesAttributes } from './modify-multiple-entities-command';

export class GtthCrudMultipleEntitiesCommand extends GtthAbstractCommand {
    private createEntitiesCommand: GtthCreateMultipleEntitiesCommand;
    private modifyMultipleEntitiesCommand: GtthModifyNodesAttributes;
    private removeLosesCommand: GtthRemoveMultipleEntitiesCommand;
    private createLinksAndLosesCommand: GtthCreateMultipleEntitiesCommand;
    private newLinksAndLoses: GtthLinkFeatureDtoModel[];

    private forcedLinksId: number[];

    createForCustomImport(
        nodesToCreate: GtthNodeFeatureDtoModel[],
        nodesToUpdate: GtthNodeFeatureDtoModel[],
        linkIdsToRemove: Set<number>,
        linksToCreate: GtthLinkFeatureDtoModel[]
    ): this {
        this.newLinksAndLoses = linksToCreate;

        if (linkIdsToRemove && linkIdsToRemove.size > 0) {
            const idsToRemoveArray = [];
            linkIdsToRemove.forEach((linkId) => idsToRemoveArray.push(linkId));
            this.removeLosesCommand = this.historyService.command(GtthRemoveMultipleEntitiesCommand).create([], idsToRemoveArray);
        }

        if (nodesToCreate && nodesToCreate.length > 0) {
            this.createEntitiesCommand = this.historyService.command(GtthCreateMultipleEntitiesCommand).create(nodesToCreate, []);
        }

        if (nodesToUpdate && nodesToUpdate.length > 0) {
            this.modifyMultipleEntitiesCommand = this.historyService.command(GtthModifyNodesAttributes).create(nodesToUpdate);
        }

        return this;
    }

    getCommandName(): string {
        return 'GtthCrudMultipleEntitiesCommand';
    }

    private async runDoCommands(): Promise<boolean> {
        let result = true;
        this.forcedLinksId = [];

        if (this.removeLosesCommand) {
            result = result && (await this.historyService.doCommandBypassHistory(this.removeLosesCommand));
        }

        if (this.modifyMultipleEntitiesCommand) {
            result = result && (await this.historyService.doCommandBypassHistory(this.modifyMultipleEntitiesCommand));
        }

        if (result && this.createEntitiesCommand) {
            result = result && (await this.historyService.doCommandBypassHistory(this.createEntitiesCommand));
        }

        if (this.newLinksAndLoses && this.newLinksAndLoses.length > 0) {
            this.newLinksAndLoses.forEach((link) => {
                if (link.getIsUserForced()) {
                    this.forcedLinksId.push(link.getId());
                }
            });
            this.createLinksAndLosesCommand = this.historyService
                .command(GtthCreateMultipleEntitiesCommand)
                .create([], this.newLinksAndLoses);

            result = result && (await this.historyService.doCommandBypassHistory(this.createLinksAndLosesCommand));
        }

        if (this.forcedLinksId.length > 0) {
            this.historyService.logicalTopology.redrawSectors(this.historyService);
        }

        return result;
    }

    private async runUndoCommands(): Promise<boolean> {
        let result = true;

        if (this.forcedLinksId.length > 0) {
            this.forcedLinksId.forEach((linkId) => this.historyService.logicalTopology.getLink(linkId).setUserForced(false));
        }

        if (this.createLinksAndLosesCommand) {
            result = result && (await this.historyService.undoCommandBypassHistory(this.createLinksAndLosesCommand));
        }

        if (result && this.createEntitiesCommand) {
            result = result && (await this.historyService.undoCommandBypassHistory(this.createEntitiesCommand));
        }

        if (this.modifyMultipleEntitiesCommand) {
            result = result && (await this.historyService.undoCommandBypassHistory(this.modifyMultipleEntitiesCommand));
        }

        if (this.removeLosesCommand) {
            result = result && (await this.historyService.undoCommandBypassHistory(this.removeLosesCommand));
        }

        if (this.forcedLinksId.length > 0) {
            this.historyService.logicalTopology.redrawSectors(this.historyService);
        }

        return result;
    }

    doCommand(): void {
        this.historyService.clearSelectionCloseWindowAndArea();
        this.runDoCommands().then((result: boolean) => {
            const activity = this.createActivityDTO(false);
            activity.doLocally = true;
            activity.successful = result;
            this.sendActivity(activity);
        });
    }

    undoCommand(): void {
        this.historyService.clearSelectionCloseWindowAndArea();
        this.runUndoCommands().then((result: boolean) => {
            const activity = this.createActivityDTO(false);
            activity.doLocally = true;
            activity.successful = result;
            this.sendActivity(activity);
        });
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    undoCommandActivity(_activityDto: ActivityDTO): void {
        if (this.commandResolve) {
            this.commandResolve(true);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return true;
    }
}
